import { cn } from "@/lib/utils";
import { Sparkles, AlertCircle, Inbox } from "lucide-react";
import { Link } from "@tanstack/react-router";

export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  eyebrow?: string;
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
      <div>
        {eyebrow && (
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--primary)] mb-1.5">
            {eyebrow}
          </div>
        )}
        <h1 className="text-[26px] leading-tight font-bold text-anchor">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({
  className,
  children,
  accent,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { accent?: boolean }) {
  return (
    <div
      {...rest}
      className={cn(
        "relative bg-surface rounded-[14px] border border-soft shadow-card",
        accent && "before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-[3px] before:rounded-r before:bg-[var(--primary)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function KpiCard({
  label,
  value,
  delta,
  trend = "up",
}: {
  label: string;
  value: string;
  delta?: string;
  trend?: "up" | "down" | "flat";
}) {
  const color = trend === "up" ? "text-[var(--success)]" : trend === "down" ? "text-[var(--danger)]" : "text-muted-foreground";
  return (
    <Card className="p-5 overflow-hidden">
      <div className="absolute top-0 left-0 h-1 w-12 bg-[var(--primary)] rounded-br" />
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--primary)]">{label}</div>
      <div className="mt-2 text-[28px] font-bold text-anchor leading-none">{value}</div>
      {delta && <div className={cn("mt-2 text-xs font-medium", color)}>{delta}</div>}
    </Card>
  );
}

type Tone = "success" | "warning" | "danger" | "info" | "ai" | "neutral" | "azure";

export function StatusBadge({ tone = "neutral", children, dot = false }: { tone?: Tone; children: React.ReactNode; dot?: boolean }) {
  const map: Record<Tone, string> = {
    success: "bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]/20",
    warning: "bg-[var(--warning)]/10 text-[#9a6500] border-[var(--warning)]/20",
    danger: "bg-[var(--danger)]/10 text-[var(--danger)] border-[var(--danger)]/20",
    info: "bg-[var(--info-soft)] text-[var(--anchor)] border-[var(--info)]",
    ai: "bg-[var(--ai)]/10 text-[var(--ai)] border-[var(--ai)]/20",
    azure: "bg-[var(--azure)]/10 text-[var(--azure)] border-[var(--azure)]/20",
    neutral: "bg-muted text-muted-foreground border-soft",
  };
  const dotMap: Record<Tone, string> = {
    success: "bg-[var(--success)]",
    warning: "bg-[var(--warning)]",
    danger: "bg-[var(--danger)]",
    info: "bg-[var(--azure)]",
    ai: "bg-[var(--ai)]",
    azure: "bg-[var(--azure)]",
    neutral: "bg-muted-foreground",
  };
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium", map[tone])}>
      {dot && <span className={cn("h-1.5 w-1.5 rounded-full", dotMap[tone])} />}
      {children}
    </span>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
}) {
  const v = {
    primary: "bg-[var(--primary)] text-[color:var(--primary-foreground)] hover:brightness-95",
    secondary: "bg-surface text-anchor border border-[var(--anchor)]/30 hover:border-[var(--anchor)]",
    ghost: "text-anchor hover:bg-muted",
    danger: "bg-[var(--danger)] text-white hover:brightness-95",
  }[variant];
  const s = size === "sm" ? "h-8 px-3 text-xs" : "h-9 px-4 text-sm";
  return (
    <button {...props} className={cn("inline-flex items-center gap-1.5 rounded-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ring-focus", v, s, className)}>
      {children}
    </button>
  );
}

export function AiTag({ children = "Auto-filled" }: { children?: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--ai)]/10 text-[var(--ai)] border border-[var(--ai)]/20 px-2 py-0.5 text-[11px] font-medium">
      <Sparkles className="h-3 w-3" />
      {children}
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  body?: string;
  action?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="text-center py-16 px-6 rounded-[14px] border border-dashed border-strong bg-surface/50">
      <div className="mx-auto h-12 w-12 rounded-xl bg-[var(--info-soft)] grid place-items-center mb-4">
        <Icon className="h-5 w-5 text-[var(--primary)]" />
      </div>
      <h3 className="text-base font-semibold text-anchor">{title}</h3>
      {body && <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-[12px] border border-[var(--danger)]/20 bg-[var(--danger)]/5">
      <AlertCircle className="h-5 w-5 text-[var(--danger)] shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-medium text-[var(--danger)]">Something went wrong</div>
        <div className="text-xs text-muted-foreground mt-0.5">{message}</div>
      </div>
      {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Retry</Button>}
    </div>
  );
}

export function GeoAccent({ className }: { className?: string }) {
  return (
    <svg className={cn("absolute pointer-events-none opacity-[0.06]", className)} viewBox="0 0 200 200" fill="none">
      <polygon points="100,10 190,55 190,145 100,190 10,145 10,55" stroke="currentColor" strokeWidth="1" />
      <polygon points="100,40 160,72 160,128 100,160 40,128 40,72" stroke="currentColor" strokeWidth="1" />
      <circle cx="100" cy="100" r="30" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

export function CornerTag({ label = "AI" }: { label?: string }) {
  return (
    <span className="absolute -top-2 -right-2 rounded-md bg-[var(--primary)] text-[color:var(--primary-foreground)] text-[10px] font-semibold px-1.5 py-0.5 shadow-card">
      {label}
    </span>
  );
}

export function NavCrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="text-xs text-muted-foreground mb-2">
      {items.map((i, idx) => (
        <span key={idx}>
          {i.to ? (
            <Link to={i.to} className="hover:text-anchor">{i.label}</Link>
          ) : (
            <span className="text-anchor font-medium">{i.label}</span>
          )}
          {idx < items.length - 1 && <span className="mx-1.5 text-[color:var(--border-strong)]">/</span>}
        </span>
      ))}
    </nav>
  );
}
